import { flatten } from "lodash";
import { calculateMarkedUpPricing } from "../inventory/pricing";
export const createUniqueFulfillmentGroupKey = (group) => `${group.fulfillment.profileId}-${group.fulfillment.type === "physical" &&
    `${group.fulfillment.modality}-${group.fulfillment.date}`}-${group.fulfillment.type}`;
/** Calculates the tax burden of all items in a group, using the existing applicableTaxes
 * array. Returns an array of numbers representing amounts paid in cents for each tax,
 * respectively.
 */
export const calculateTaxBurden = ({ items, isRetail, applicableTaxes, }) => {
    const subTotal = calculateItemGroupPrice(items, isRetail);
    return applicableTaxes.map((t) => Math.ceil((t.rate / 100) * subTotal));
};
/**
 * Calculate the pricing info for a fulfillment group (child order).
 * Includes tax (if necessary) and fulfillment price (i.e. shipping, delivery fee).
 *
 * @param x The fulfillment group to calculate.
 * @param isRetail Whether to use retail pricing (tax included + markups)
 */
export const calculateGroupPricing = ({ items, isRetail, fulfillment, applicableTaxes, }) => {
    const subtotal = calculateItemGroupPrice(items, isRetail);
    const handlingFee = Math.round((subtotal * (fulfillment.handlingFeePercent || 0)) / 100);
    const taxBurden = calculateTaxBurden({
        items: items,
        applicableTaxes: applicableTaxes,
        isRetail,
    });
    const taxTotal = isRetail ? taxBurden.reduce((a, b) => a + b, 0) : 0;
    const fulfillmentPrice = fulfillment.price;
    const total = subtotal + taxTotal + fulfillmentPrice + handlingFee;
    return {
        subtotal,
        taxBurden,
        fulfillmentPrice,
        handlingFee,
        total,
    };
};
/**
 * Calculate the total cost of a cart.
 * @param x The cart
 * @param isRetail Whether to use retail pricing (tax included + markups)
 */
export const calculateCartTotal = (x) => !x || x.fulfillmentGroups.length === 0
    ? 0
    : x.fulfillmentGroups.map((g) => g._calculated.total).reduce((a, b) => a + b);
/**
 * Calculate the effective price of an item.
 *
 * @param x The item
 * @param isRetail Whether retail pricing should be used
 */
export function calculateItemPrice(x, isRetail) {
    var _a, _b;
    // even cut flowers are priced this way as price is always price per unit purchaseable
    const retailMarkup = x.listing.category === "ad-hoc"
        ? 0
        : (_b = (_a = x.listing.seller._cache.categorySettings[x.listing.category]) === null || _a === void 0 ? void 0 : _a.retailMarkup) !== null && _b !== void 0 ? _b : 0;
    const pricing = isRetail
        ? calculateMarkedUpPricing(x.listing.pricing, retailMarkup)
        : x.listing.pricing;
    return pricing.price * x.quantity;
}
/** Calculate the price of a group of items, all sharing a fulfillment option. */
export function calculateItemGroupPrice(items, isRetail) {
    return items.map((i) => calculateItemPrice(i, isRetail)).reduce((a, b) => a + b, 0);
}
/**
 * Flatten a cart with multiple fulfillment groups into a single list of items.
 * @param x The cart
 */
export function allItems(x) {
    return flatten(x.fulfillmentGroups.map((g) => g.items));
}
/**
 * Calculate the total quantity of a fulfillment group in a cart / order.
 * @param x The fulfillment group
 */
export function calculateItemGroupQuantity(items) {
    return items.map((i) => i.quantity).reduce((a, b) => a + b, 0);
}
//# sourceMappingURL=carts.js.map